import { Button, Form, Select, Space, Table, theme } from "antd";
import { useCreateEnumItemMutation, useDeleteEnumItemMutation, useGetEnumItemsQuery, useGetEnumsQuery, useUpdateEnumItemMutation } from "../../../../api/plamo-api";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import EditableCell from "../../action-table/editable-cell";
import { showSuccessMessage } from "../../util/with-save-notification";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from '@dnd-kit/utilities';
import EditableContext from "../../action-table/editable-context";
import { HolderOutlined } from "@ant-design/icons";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = createContext<RowContextProps>({});

const Row: React.FC<Readonly<RowProps>> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <RowContext.Provider value={contextValue}>
          <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />
        </RowContext.Provider>
      </EditableContext.Provider>
    </Form>
  );
};

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const TableFormItem = ({value, onChange, enumId}: any) => {
  const sortedValue = useMemo(() => value?.sort((a, b) => a.orderIdx - b.orderIdx) || [], [value])
  const keyedValue = useMemo(() => sortedValue?.map((v, idx) => ({...v, key: idx})) || [], [sortedValue])

  const addRow = useCallback(() => {
    const newRows = [...(value || []), { label: '', value: ' ', referencedEnum: { id: enumId } }]
    onChange(newRows);
  }, [value, onChange, enumId])

  const deleteRow = useCallback((row) => {
    const newRows = value.filter((_, idx) => idx !== row.key);
    console.log("Deleted row", row, newRows);
    onChange(newRows);
  }, [value, onChange])

  const handleSaveItem = useCallback((item) => {
    const newItem = {...item}
    newItem.label = newItem.label.trim();
    if(newItem.label.length > 0 && newItem.value.trim().length === 0) {
      newItem.value = newItem.label;
    }
    const newRows = value.map((i, idx) => idx === newItem.key ? newItem : i);
    onChange(newRows);
  }, [value, onChange])

  const columns = useMemo(() => [
    { 
      key: 'sort',
      dataIndex: 'id',
      width: 80, 
      render: () => <DragHandle /> 
    },
    {
      title: 'Bezeichnung',
      dataIndex: 'label',
      key: 'label',
      onCell: record => ({
        record,
        editable: true,
        dataIndex: 'label',
        title: 'Bezeichnung',
        handleSave: handleSaveItem,
        type: 'string',
      })
    },
    {
      title: 'Wert',
      dataIndex: 'value',
      key: 'value',
      type: 'string',
      editable: true,
      onCell: record => ({
        record,
        editable: true,
        dataIndex: 'value',
        title: 'Wert',
        handleSave: handleSaveItem,
        type: 'string',
      })
    },
    {
      title: 'Aktionen',
      render: (item) => <Button onClick={() => deleteRow(item)} danger>Löschen</Button>
    } 
  ], [handleSaveItem, deleteRow]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = keyedValue.findIndex((i) => i.key === active.id);
      const overIndex = keyedValue.findIndex((i) => i.key === over?.id);
      const newList = arrayMove(keyedValue, activeIndex, overIndex).map((v: any, orderIdx) => ({ ...v, orderIdx }))
      onChange(newList);
    }
  };

  return (
    <>
      <div style={{ width: "100%", textAlign: "right" }}><Button type="primary" onClick={addRow}>Neuer Aufzählungswert</Button></div>
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={keyedValue.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            size="small" 
            components={{
              body: { 
                row: Row,
                cell: EditableCell,
              },
            }}
            rowKey="key"
            columns={columns}
            dataSource={keyedValue}
            pagination={false}
          />
        </SortableContext>
      </DndContext>
    </>
  );
}

function Enums() {
  const [form] = Form.useForm()
  const [selectedEnumId, setSelectedEnumId] = useState<number | null>(null);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const { data: enums } = useGetEnumsQuery({ st: JSON.stringify({ label: "asc" })}) as any;
  const { data: enumItems } = useGetEnumItemsQuery({ f: JSON.stringify({ "referencedEnum.id": { c: "EQ", v: selectedEnumId } }), st: JSON.stringify({ orderIdx: "asc"})}, { skip: !selectedEnumId }) as any;
  const [createEnumItem] = useCreateEnumItemMutation();
  const [updateEnumItem] = useUpdateEnumItemMutation();
  const [deleteEnumItem] = useDeleteEnumItemMutation();

  useEffect(() => {
    form.setFieldsValue({ items: enumItems?.items })
  }, [form, enumItems?.items])

  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
      maxWidth: 'none',
      background: token.colorFillAlter,
      borderRadius: token.borderRadiusLG,
      padding: 24,
    };

  const onFinish = useCallback(async(values) => {
    setSavingChanges(true)
    const newValues = (values?.items || []).map((v, idx) => ({ ...v, orderIdx: idx }))
    const updatePromises = newValues.filter(v => {
      const existingItem = enumItems?.items?.find(i => i.id === v.id)
      return !existingItem || v.label !== existingItem.label || v.value !== existingItem.value || v.orderIdx !== existingItem.orderIdx;
    }).map(v => v.id ? updateEnumItem(v) : createEnumItem(v))
    
    const deletePromises = (enumItems?.items || []).filter(v => !values?.items?.find(i => i.id === v.id)).map(v => deleteEnumItem(v));

    await Promise.all([...updatePromises, ...deletePromises]);
    setSavingChanges(false);
    showSuccessMessage();
  }, [enumItems, updateEnumItem, createEnumItem, deleteEnumItem])

  return (
    <>
    <h2>Aufzählungslisten</h2>
    
    <Space direction="vertical" style={{ width: '100%' }}>
      <Select style={{ width: "20em" }} options={enums?.items?.map(e => ({ label: e.label, value: e.id }))} value={selectedEnumId} onChange={setSelectedEnumId} />
      { selectedEnumId && 
      <Form form={form} name="employee_main" style={formStyle} onFinish={onFinish}>
        <Form.Item 
          name="items"
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}>
            <TableFormItem enumId={selectedEnumId} />
        </Form.Item>
        <div style={{ textAlign: 'right' }}>
          <Space size="small">
            <Button loading={savingChanges} type="primary" htmlType="submit">
              Speichern
            </Button>
          </Space>
        </div>
      </Form>
      }
    </Space>
    </>
  )
}

export default Enums;